import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CreditCard, Banknote, Wallet, FileText, Receipt, CheckCircle2, Lock } from "lucide-react";

export default function Billing() {
  const { formatApiError } = useAuth();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pm, setPm] = useState({ payment_method: "ach_pull", payment_last4: "" });

  const load = async () => {
    try {
      const [b, inv] = await Promise.all([
        api.get("/billing/me").then((r) => r.data),
        api.get("/billing/invoices").then((r) => r.data),
      ]);
      setData(b); setInvoices(inv);
      setPm({
        payment_method: b.billing.payment_method || "ach_pull",
        payment_last4: b.billing.payment_last4 || "",
      });
    } catch (e) { toast.error("Could not load billing"); }
  };
  useEffect(() => { load(); }, []);

  const savePaymentMethod = async () => {
    try {
      await api.post("/billing/me/payment-method", pm);
      toast.success("Payment method saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading billing...</div>;

  const { billing, resolved, plan } = data;

  return (
    <div data-testid="billing-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[#00D533] font-semibold">Billing & Membership</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2 text-slate-900">Your Plan</h1>
      <p className="text-slate-600 mt-2 max-w-2xl">
        Each merchant has an independent plan and billing structure. Defaults apply unless your account has custom pricing
        configured by an underwriter.
      </p>

      {/* Plan summary */}
      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <PlanCard
          title={plan?.name || billing.plan_id}
          subtitle={plan?.description}
          tag={billing.custom ? "Custom pricing" : "Default pricing"}
          tagColor={billing.custom ? "#C8A046" : "#00D533"}
        />
        <KPI label="Setup fee" value={`$${(billing.setup_fee_override ?? plan?.setup_fee ?? 0).toLocaleString()}`} icon={FileText} />
        <KPI label="Monthly minimum" value={`$${(billing.monthly_minimum_override ?? plan?.monthly_minimum ?? 0).toLocaleString()}`} icon={Wallet} accent="#00D533" />
      </div>

      <Tabs defaultValue="modules" className="mt-10">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="modules" data-testid="billing-tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="payment" data-testid="billing-tab-payment">Payment Method</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="modules" className="mt-6">
          <div className="sk-card overflow-hidden" data-testid="billing-modules-table">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <div className="col-span-4">Module</div>
              <div className="col-span-2 text-right">Monthly</div>
              <div className="col-span-2 text-right">% rate</div>
              <div className="col-span-2 text-right">Flat / tx</div>
              <div className="col-span-2 text-right">Effective</div>
            </div>
            {resolved.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No modules enabled.</div>}
            {resolved.map((m) => (
              <div key={m.module_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-200">
                <div className="col-span-4">
                  <div className="text-slate-900 font-medium">{m.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{m.module_id}</div>
                </div>
                <div className="col-span-2 text-right font-mono">${m.pricing.monthly.toFixed(2)}</div>
                <div className="col-span-2 text-right font-mono">{m.pricing.percent_rate.toFixed(2)}%</div>
                <div className="col-span-2 text-right font-mono">${m.pricing.flat_per_tx.toFixed(2)}</div>
                <div className="col-span-2 text-right">
                  <span className="px-2 py-0.5 rounded text-xs uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Want to add or remove modules? Contact your underwriter — module changes are managed by SelleCheck Admin.
          </p>
        </TabsContent>

        <TabsContent value="payment" className="mt-6">
          <div className="sk-card p-6 max-w-xl space-y-4" data-testid="payment-method-form">
            <h3 className="font-heading text-lg text-slate-900">How you pay your SelleCheck bill</h3>
            <div>
              <Label className="text-slate-700">Method</Label>
              <Select value={pm.payment_method} onValueChange={(v) => setPm({ ...pm, payment_method: v })}>
                <SelectTrigger className="mt-2 bg-white border-slate-200 h-11" data-testid="pm-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900">
                  <SelectItem value="ach_pull">ACH Pull (recommended)</SelectItem>
                  <SelectItem value="wire">Wire transfer</SelectItem>
                  <SelectItem value="card_on_file">Card on file (via Gateway)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Last 4 (account or card)</Label>
              <Input value={pm.payment_last4} maxLength={4} onChange={(e) => setPm({ ...pm, payment_last4: e.target.value })}
                className="mt-2 bg-white border-slate-200 h-11 font-mono" placeholder="1234"
                data-testid="pm-last4" />
            </div>
            <div className="flex items-start gap-2 text-xs text-slate-500 pt-2 border-t border-slate-200">
              <Lock className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
              <span>Card details on file are tokenized in the SelleCheck Transaction Gateway vault — not stored here.</span>
            </div>
            <Button onClick={savePaymentMethod} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="pm-save-btn">
              Save payment method
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          <div className="sk-card overflow-hidden" data-testid="invoices-table">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <div className="col-span-3">Invoice</div>
              <div className="col-span-3">Period</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Lines</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {invoices.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No invoices yet.</div>}
            {invoices.map((inv) => (
              <details key={inv.invoice_id} className="border-b border-slate-200">
                <summary className="grid grid-cols-12 items-center px-4 py-3 text-sm cursor-pointer hover:bg-slate-50">
                  <div className="col-span-3 font-mono text-xs text-slate-600">{inv.invoice_id}</div>
                  <div className="col-span-3 text-slate-700">{fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}</div>
                  <div className="col-span-2">
                    <span className={`px-2 py-0.5 rounded text-xs uppercase tracking-wider ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{inv.status}</span>
                  </div>
                  <div className="col-span-2 text-right font-mono text-slate-500">{inv.lines.length}</div>
                  <div className="col-span-2 text-right font-mono font-semibold">${inv.total.toFixed(2)}</div>
                </summary>
                <div className="px-4 pb-4 pt-2 bg-slate-50">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left py-1">Line</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Amount</th></tr>
                    </thead>
                    <tbody>
                      {inv.lines.map((l, i) => (
                        <tr key={i} className="border-t border-slate-200">
                          <td className="py-1.5">{l.name}</td>
                          <td className="text-right font-mono">{l.qty}</td>
                          <td className="text-right font-mono">${Number(l.unit).toFixed(2)}</td>
                          <td className="text-right font-mono">${Number(l.amount).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlanCard({ title, subtitle, tag, tagColor }) {
  return (
    <div className="sk-card p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">Plan</div>
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider"
          style={{ background: `${tagColor}1A`, color: tagColor, border: `1px solid ${tagColor}33` }}>
          {tag}
        </span>
      </div>
      <div className="mt-2 font-heading text-2xl text-slate-900 capitalize">{title}</div>
      {subtitle && <div className="mt-1 text-sm text-slate-500">{subtitle}</div>}
    </div>
  );
}
function KPI({ label, value, accent = "#0F172A", icon: Icon }) {
  return (
    <div className="sk-card p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="mt-2 font-heading text-2xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}
function fmtDate(s) { try { return new Date(s).toLocaleDateString(); } catch { return s; } }
